/**
 * Utilization Settings Component
 * Allows configuration of organization-wide utilization targets and calculation methods
 */

import React, { useState, useEffect } from 'react';
import { Label } from '@/components/ui/label';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { TrendingUp, Calculator } from 'lucide-react';
import type { UtilisationSettings as UtilisationSettingsType } from '@/types/timesheetSettings';

interface UtilisationSettingsProps {
  settings: UtilisationSettingsType;
  onChange?: (settings: UtilisationSettingsType, isValid: boolean) => void;
  onSave?: (settings: UtilisationSettingsType) => void;
}

export const UtilisationSettings: React.FC<UtilisationSettingsProps> = ({
  settings,
  onChange,
}) => {
  const [localSettings, setLocalSettings] = useState<UtilisationSettingsType>(settings);

  // Notify parent of changes
  useEffect(() => {
    if (onChange) {
      // Validate settings
      const isValid = 
        localSettings.targetUtilisationRate >= 0 &&
        localSettings.targetUtilisationRate <= 1 &&
        ['billable-total', 'billable-available', 'billable-expected'].includes(localSettings.calculationMethod);
      
      onChange(localSettings, isValid);
    }
  }, [localSettings, onChange]);

  // Update local state when prop changes (for reset functionality)
  useEffect(() => {
    setLocalSettings(settings);
  }, [settings]);

  const handleChange = (field: keyof UtilisationSettingsType, value: any) => {
    setLocalSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Convert rate to percentage for display
  const ratePercentage = Math.round(localSettings.targetUtilisationRate * 100);

  // Get calculation method description
  const getMethodDescription = (method: string): string => {
    switch (method) {
      case 'billable-total':
        return 'Billable hours divided by total hours worked (including non-billable time)';
      case 'billable-available':
        return 'Billable hours divided by available hours (excludes PTO and holidays)';
      case 'billable-expected':
        return 'Billable hours divided by expected hours from working pattern (good for part-time staff)';
      default:
        return '';
    }
  };

  return (
    <div className="space-y-6">
      {/* Target Utilisation Rate Section */}
      <div>
        <div className="flex items-start gap-3 pb-4 border-b">
          <div className="rounded-lg bg-primary/10 p-2.5">
            <TrendingUp className="h-5 w-5 text-primary" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold">Target Utilisation Rate</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Set organisation-wide utilisation targets for performance tracking
            </p>
          </div>
        </div>

        <div className="mt-6 border rounded-lg p-4 space-y-4">
          <Label className="text-sm font-medium">Target Percentage</Label>
          
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-3xl font-bold text-primary">{ratePercentage}%</span>
              <span className="text-sm text-muted-foreground">
                {ratePercentage < 60 && 'Low target'}
                {ratePercentage >= 60 && ratePercentage < 75 && 'Moderate target'}
                {ratePercentage >= 75 && ratePercentage < 85 && 'Standard target'}
                {ratePercentage >= 85 && 'High target'}
              </span>
            </div>
            
            <Slider
              value={[localSettings.targetUtilisationRate * 100]}
              onValueChange={(value) => handleChange('targetUtilisationRate', value[0] / 100)}
              min={0}
              max={100}
              step={5}
              className="w-full"
            />
            
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>0%</span>
              <span>50%</span>
              <span>100%</span>
            </div>
          </div>

          <div className="pt-4 border-t space-y-2">
            <p className="text-xs font-medium text-muted-foreground">Typical targets by role:</p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs text-muted-foreground">
              <li>• <strong className="text-foreground">Consultants & Contractors:</strong> 75-85%</li>
              <li>• <strong className="text-foreground">Project Managers:</strong> 60-70%</li>
              <li>• <strong className="text-foreground">Technical Leads:</strong> 50-60%</li>
              <li>• <strong className="text-foreground">Support Staff:</strong> 40-50%</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Calculation Method Section */}
      <div className="pt-6">
        <div className="flex items-start gap-3 pb-4 border-b">
          <div className="rounded-lg bg-primary/10 p-2.5">
            <Calculator className="h-5 w-5 text-primary" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold">Calculation Method</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Choose how utilisation percentages are calculated across the organisation
            </p>
          </div>
        </div>

        <div className="mt-6 border rounded-lg p-4 space-y-4">
          <Label htmlFor="calculation-method" className="text-sm font-medium">
            Method
          </Label>
          
          <Select
            value={localSettings.calculationMethod}
            onValueChange={(value) => handleChange('calculationMethod', value as UtilisationSettingsType['calculationMethod'])}
          >
            <SelectTrigger id="calculation-method" className="h-10">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="billable-total">Billable / Total Hours</SelectItem>
              <SelectItem value="billable-available">Billable / Available Hours</SelectItem>
              <SelectItem value="billable-expected">Billable / Expected Hours</SelectItem>
            </SelectContent>
          </Select>

          <p className="text-xs text-muted-foreground">
            {getMethodDescription(localSettings.calculationMethod)}
          </p>

          <div className="pt-4 border-t space-y-3">
            <p className="text-xs font-medium text-muted-foreground">How calculation methods differ:</p>
            
            <div className="space-y-2">
              <div className="flex items-start gap-2">
                <div className="w-2 h-2 rounded-full bg-blue-500 mt-1.5 flex-shrink-0" />
                <div className="flex-1 space-y-1">
                  <p className="text-xs font-medium text-foreground">Billable / Total Hours</p>
                  <p className="text-xs text-muted-foreground">
                    Best for organisations tracking all time. Penalises non-billable work.
                  </p>
                  <p className="text-xs text-muted-foreground italic">
                    Example: 30 billable + 10 non-billable = 75% utilisation
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-2">
                <div className="w-2 h-2 rounded-full bg-green-500 mt-1.5 flex-shrink-0" />
                <div className="flex-1 space-y-1">
                  <p className="text-xs font-medium text-foreground">Billable / Available Hours</p>
                  <p className="text-xs text-muted-foreground">
                    Best for fair assessment when employees take PTO. Excludes time off.
                  </p>
                  <p className="text-xs text-muted-foreground italic">
                    Example: 30 billable + 10 non-billable + 8 PTO = 75% (PTO ignored)
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-2">
                <div className="w-2 h-2 rounded-full bg-purple-500 mt-1.5 flex-shrink-0" />
                <div className="flex-1 space-y-1">
                  <p className="text-xs font-medium text-foreground">Billable / Expected Hours</p>
                  <p className="text-xs text-muted-foreground">
                    Best for part-time staff or flexible schedules. Uses working pattern as baseline.
                  </p>
                  <p className="text-xs text-muted-foreground italic">
                    Example: 30 billable hours vs 40 expected = 75% utilisation
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="pt-4 border-t space-y-2">
            <p className="text-xs font-medium text-muted-foreground">Current configuration:</p>
            <ul className="text-xs text-muted-foreground space-y-1">
              <li>• Target rate: <span className="font-medium text-foreground">{ratePercentage}%</span></li>
              <li>• Calculation: <span className="font-medium text-foreground">
                {localSettings.calculationMethod === 'billable-total' && 'Billable / Total Hours'}
                {localSettings.calculationMethod === 'billable-available' && 'Billable / Available Hours'}
                {localSettings.calculationMethod === 'billable-expected' && 'Billable / Expected Hours'}
              </span></li>
              <li>• Applied to: <span className="font-medium text-foreground">All employees and reports</span></li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};
